(ns frontend.view.settings2.security.ai-admin.view
  "AI services admin panel — tenant admins configure per-tenant AI providers,
   the default service, and per-task-type routing + token-warning email."
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.schema.entities.integration.ai-options :as ai-options]
   [frontend.utils :as fu]
   [frontend.view.settings2.security.ai-admin.event :as event]
   [frontend.view.settings2.security.ai-admin.sub :as sub]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.input.file-input :as file-input]
   [ui.dom-globals :as dg]
   [ui.editors.labelled-input.view :as labelled-input]))

(defn- service-label
  [service]
  (let [label (:label service)]
    (if (string/blank? label)
      (str (some-> (:provider service) name) " / " (:model service))
      label)))

(defn- test-outcome-copy
  [outcome]
  (condp = outcome
    ai-options/VERIFICATION-VERIFIED        :settings/ai-admin-test-verified
    ai-options/VERIFICATION-RATE-LIMITED    :settings/ai-admin-test-rate-limited
    ai-options/VERIFICATION-MODEL-NOT-FOUND :settings/ai-admin-test-model-not-found
    ai-options/VERIFICATION-KEY-REJECTED    :settings/ai-admin-test-key-rejected
    ai-options/VERIFICATION-NO-CREDIT       :settings/ai-admin-test-no-credit
    ai-options/VERIFICATION-MISSING-API-KEY :settings/ai-admin-test-missing-api-key
    ;; Also the landing place for :request-failed, the view-only marker for an
    ;; RPC that never reached the provider.
    :settings/ai-admin-test-failed))

(defn- test-passed?
  [outcome]
  (contains? ai-options/verification-passed outcome))

(defn- test-result-view
  [{:keys [outcome model provider-message]}]
  [:div.mt-3.text-sm
   {:class (if (test-passed? outcome)
             "text-success-regular"
             "text-danger-regular")}
   [:i.mr-1
    {:class (if (test-passed? outcome)
              "fal fa-check"
              "fal fa-exclamation-triangle")}]
   (loc/tr (test-outcome-copy outcome) [(or model "")])
   ;; Provider-authored text: rendered as a plain string, never as markup.
   (when-not (string/blank? provider-message)
     [:div.mt-1.text-xs.text-base-light-text-paler
      provider-message])])

(defn- verification-badge
  "In-session health of a stored service. Absent means not verified in this
   session, which reads as unknown rather than bad - nothing is persisted yet."
  [service-id]
  (let [{:keys [outcome model verified-on]}
        @(rf/subscribe [::sub/verification service-id])]
    (cond
      (nil? outcome)
      nil

      (= :pending outcome)
      [:span.text-xs.text-base-light-text-paler
       [:i.fal.fa-spinner.fa-pulse.mr-1]
       (loc/tr :settings/ai-admin-testing)]

      (test-passed? outcome)
      [:span.text-xs.text-success-regular
       {:title (loc/tr (test-outcome-copy outcome) [(or model "")])}
       [:i.fal.fa-check.mr-1]
       (if verified-on
         (loc/tr :settings/ai-admin-verified-badge-on
                 [(fu/format-date-ts verified-on)])
         (loc/tr :settings/ai-admin-verified-badge))]

      :else
      ;; One neutral label covers every failure mode - a mistyped model, a
      ;; rejected key, no key at all, no credit - because "not reachable" is
      ;; wrong for most of them. The specific reason is in the tooltip.
      [:span.text-xs.text-danger-regular
       {:title (loc/tr (test-outcome-copy outcome) [(or model "")])}
       [:i.fal.fa-exclamation-triangle.mr-1]
       (loc/tr :settings/ai-admin-unverified-badge)])))

(defn- service-editor
  []
  (let [editing   @(rf/subscribe [::sub/editing])
        providers @(rf/subscribe [::sub/providers])
        saving?   @(rf/subscribe [::sub/saving?])
        testing?  @(rf/subscribe [::sub/testing?])
        test-result @(rf/subscribe [::sub/test-result])
        set-field! (fn [k] (fn [v] (rf/dispatch [::event/update-edit k v])))]
    (when editing
      [:div.border.rounded-lg.p-4.mb-4
       {:class "bg-base-lightest"}
       [:div.font-semibold.mb-3
        (loc/tr (if (:id editing)
                  :settings/ai-admin-edit-service
                  :settings/ai-admin-new-service))]
       [:div.grid.grid-cols-2.gap-3
        [labelled-input/view
         {:label (loc/tr :settings/ai-admin-label) :data-type :data.type/text-simple
          :value (:label editing) :on-change (set-field! :label)}]
        [labelled-input/view
         {:label (loc/tr :settings/ai-admin-provider) :data-type :data.type/keyword
          :value (:provider editing) :options providers :on-change (set-field! :provider)}]
        [labelled-input/view
         {:label (loc/tr :settings/ai-admin-model) :data-type :data.type/text-simple
          :value (:model editing) :on-change (set-field! :model)}]
        [:div
         [labelled-input/view
          {:label (loc/tr :settings/ai-admin-base-url) :data-type :data.type/text-simple
           :value (:base-url editing) :on-change (set-field! :base-url)}]
         (when (= :ai.provider/copilot (:provider editing))
           [:div.text-xs.text-base-light-text-paler.mt-1
            (loc/tr :settings/ai-admin-base-url-hint-copilot)])]
        [labelled-input/view
         {:label (loc/tr (if (:has-api-key? editing)
                           :settings/ai-admin-api-key-keep
                           :settings/ai-admin-api-key))
          :data-type :data.type/text-simple
          :value (:api-key editing) :on-change (set-field! :api-key)}]
        [labelled-input/view
         {:label (loc/tr :settings/ai-admin-enabled) :data-type :data.type/boolean
          :value (boolean (:enabled? editing)) :on-change (set-field! :enabled?)}]]
       [:div.flex.gap-2.mt-3
        [button/sm-primary-solid
         {:disabled? (or saving? testing?)
          :on-click #(rf/dispatch [::event/save-service])}
         (loc/tr (if saving? :settings/ai-admin-saving :settings/ai-admin-save))]
        [button/sm-secondary-hollow
         {:disabled? (or saving?
                         testing?
                         (string/blank? (:model editing)))
          :on-click #(rf/dispatch [::event/test-service])}
         (loc/tr (if testing?
                   :settings/ai-admin-testing
                   :settings/ai-admin-test))]
        [button/sm-secondary-hollow
         {:on-click #(rf/dispatch [::event/cancel-edit])}
         (loc/tr :settings/ai-admin-cancel)]]
       (when test-result
         [test-result-view test-result])])))

(defn- services-table
  []
  (let [services   @(rf/subscribe [::sub/services])
        default-id @(rf/subscribe [::sub/default-service-id])]
    [:div
     [:div.flex.items-center.justify-between.mb-2
      [:div.font-semibold (loc/tr :settings/ai-admin-title)]
      [button/sm-primary-solid
       {:on-click #(rf/dispatch [::event/new-service])}
       (loc/tr :settings/ai-admin-add-service)]]
     (if (empty? services)
       [:div.text-base-light-text-paler (loc/tr :settings/ai-admin-no-services)]
       [:table.w-full.text-sm
        [:thead
         [:tr.text-left.border-b
          [:th.py-1 (loc/tr :settings/ai-admin-label)]
          [:th (loc/tr :settings/ai-admin-provider)]
          [:th (loc/tr :settings/ai-admin-model)]
          [:th (loc/tr :settings/ai-admin-enabled)]
          [:th (loc/tr :settings/ai-admin-col-default)]
          [:th (loc/tr :settings/ai-admin-col-status)]
          [:th ""]]]
        [:tbody
         (for [s services]
           ^{:key (:id s)}
           [:tr.border-b.border-base-lighter
            [:td.py-1 (service-label s)]
            [:td (some-> (:provider s) name)]
            [:td (:model s)]
            [:td (loc/tr (if (:enabled? s) :settings/ai-admin-yes :settings/ai-admin-no))]
            [:td [:input {:type "radio"
                          :checked (= (:id s) default-id)
                          :on-change #(rf/dispatch [::event/set-default (:id s)])}]]
            [:td [verification-badge (:id s)]]
            [:td.text-right
             [:div.flex.gap-2.justify-end
              [button/sm-secondary-hollow
               {:on-click #(rf/dispatch [::event/edit-service s])}
               (loc/tr :settings/ai-admin-edit)]
              [button/sm-danger-solid
               {:on-click #(rf/dispatch [::event/delete-service (:id s)])}
               (loc/tr :settings/ai-admin-delete)]]]])]])
     (when default-id
       [button/sm-secondary-hollow
        {:class "mt-2"
         :on-click #(rf/dispatch [::event/set-default nil])}
        (loc/tr :settings/ai-admin-clear-default)])]))

(defn- template-upload-control
  [{:keys [scope task-type pipeline-id draft]}]
  (let [upload-key {:scope scope
                    :task-type task-type
                    :pipeline-id pipeline-id}
        uploading-map @(rf/subscribe [::sub/template-uploading])
        uploading? (boolean (get uploading-map upload-key))
        update-field! (fn [field value]
                        (if (= :pipeline scope)
                          (rf/dispatch [::event/update-task-config-override-field
                                        task-type pipeline-id field value])
                          (rf/dispatch [::event/update-task-config-field
                                        task-type field value])))]
    [:div
     [:div.text-xs.text-base-light-text-paler.mb-1
      (loc/tr :settings/ai-admin-document-template)]
     [:div.flex.items-center.gap-2.flex-wrap
      [:div.text-sm
       (or (:document-template-filename draft)
           (loc/tr :settings/ai-admin-document-template-none))]
      [file-input/view
       {:accept-file-types [:docx]
        :on-change (fn [{:keys [files]}]
                     (when-let [file (when-not uploading? (first files))]
                       (rf/dispatch [::event/upload-template
                                     (assoc upload-key :file file)])))
        :trigger [:span.cursor-pointer.text-sm.text-base-light-text-accent.font-semibold
                  (loc/tr (if uploading?
                            :settings/ai-admin-uploading-template
                            :settings/ai-admin-upload-template))]}]
      (when (:document-template-id draft)
        [button/sm-secondary-hollow
         {:disabled? uploading?
          :on-click (fn []
                      (update-field! :document-template-id nil)
                      (update-field! :document-template-filename nil))}
         (loc/tr :settings/ai-admin-clear-template)])]]))

(defn- task-config-row
  [task-type-opt]
  (let [task-type (:value task-type-opt)
        current @(rf/subscribe [::sub/task-config-draft task-type])
        services @(rf/subscribe [::sub/services])
        saving? @(rf/subscribe [::sub/saving?])
        uploading? @(rf/subscribe [::sub/template-uploading?])
        update-field! (fn [field value]
                        (rf/dispatch [::event/update-task-config-field
                                      task-type field value]))]
    [:div.py-3.border-b.border-base-lighter
     [:div.font-medium.mb-2 (:label task-type-opt)]
     [:div.grid.grid-cols-2.gap-3.items-end
      [:div
       [:label.text-xs.text-base-light-text-paler
        (loc/tr :settings/ai-admin-task-service)]
       [:select.w-full.border.rounded.p-1
        {:value (str (:service-id current))
         :on-change (fn [e]
                      (let [value (dg/event-target-value e)]
                        (update-field!
                         :service-id
                         (when-not (string/blank? value)
                           (js/parseInt value 10)))))}
        [:option
         {:value ""}
         (loc/tr :settings/ai-admin-use-default)]
        (for [service services]
          ^{:key (:id service)}
          [:option
           {:value (str (:id service))}
           (service-label service)])]]
      [:div
       [:label.text-xs.text-base-light-text-paler
        (loc/tr :settings/ai-admin-warning-email)]
       [:input.w-full.border.rounded.p-1
        {:type "email"
         :value (or (:token-warning-email current) "")
         :on-change (fn [e]
                      (update-field! :token-warning-email
                                     (dg/event-target-value e)))}]]]
     (when (= ai-options/TASK-TYPE-DEAL-ONE-PAGER task-type)
       [:div.mt-3.border.rounded-lg.p-3
        [:div.font-medium.mb-1
         (loc/tr :settings/ai-admin-one-pager-output-settings)]
        [:div.text-xs.text-base-light-text-paler.mb-3
         (loc/tr :settings/ai-admin-one-pager-output-description)]
        [:div.grid.grid-cols-2.gap-3
         [labelled-input/view
          {:label (loc/tr :settings/ai-admin-style-reference-url)
           :data-type :data.type/text-simple
           :value (or (:style-reference-url current) "")
           :on-change (fn [value]
                        (update-field! :style-reference-url value))}]
         [labelled-input/view
          {:label (loc/tr :settings/ai-admin-internal-email-domains)
           :data-type :data.type/text-simple
           :value (or (:internal-email-domains current) "")
           :on-change (fn [value]
                        (update-field! :internal-email-domains value))}]
         [template-upload-control
          {:scope :tenant
           :task-type task-type
           :draft current}]]])
     [:div.mt-3
      [button/sm-primary-solid
       {:disabled? (or saving? uploading?)
        :on-click (fn []
                    (rf/dispatch [::event/save-task-config task-type]))}
       (loc/tr (if saving?
                 :settings/ai-admin-saving
                 :settings/ai-admin-save))]]]))

(defn- pipeline-override-row
  [task-type pipeline]
  (let [pipeline-id (:id pipeline)
        current @(rf/subscribe [::sub/task-config-override-draft task-type pipeline-id])
        saving? @(rf/subscribe [::sub/saving?])
        uploading? @(rf/subscribe [::sub/template-uploading?])
        update-field! (fn [field value]
                        (rf/dispatch [::event/update-task-config-override-field
                                      task-type pipeline-id field value]))]
    [:div.border.rounded-lg.p-3
     [:div.font-medium.mb-2 (:name pipeline)]
     [:div.grid.grid-cols-2.gap-3
      [labelled-input/view
       {:label (loc/tr :settings/ai-admin-style-reference-url)
        :data-type :data.type/text-simple
        :placeholder (loc/tr :settings/ai-admin-inherit-tenant-setting)
        :value (or (:style-reference-url current) "")
        :on-change (fn [value]
                     (update-field! :style-reference-url value))}]
      [template-upload-control
       {:scope :pipeline
        :task-type task-type
        :pipeline-id pipeline-id
        :draft current}]]
     [:div.mt-3
      [button/sm-primary-solid
       {:disabled? (or saving? uploading?)
        :on-click (fn []
                    (rf/dispatch [::event/save-task-config-override
                                  task-type pipeline-id]))}
       (loc/tr (if saving?
                 :settings/ai-admin-saving
                 :settings/ai-admin-save))]]]))

(defn- pipeline-overrides
  []
  (let [pipelines @(rf/subscribe [::sub/deal-pipelines])]
    [:div.mt-6
     [:div.font-semibold.mb-1
      (loc/tr :settings/ai-admin-pipeline-overrides)]
     [:div.text-sm.text-base-light-text-paler.mb-3
      (loc/tr :settings/ai-admin-pipeline-overrides-description)]
     (if (seq pipelines)
       [:div.grid.grid-cols-2.gap-3
        (for [pipeline pipelines]
          ^{:key (:id pipeline)}
          [pipeline-override-row ai-options/TASK-TYPE-DEAL-ONE-PAGER pipeline])]
       [:div.text-base-light-text-paler
        (loc/tr :settings/ai-admin-no-deal-pipelines)])]))

(defn- panel
  []
  (let [loading? @(rf/subscribe [::sub/loading?])
        error    @(rf/subscribe [::sub/error])
        task-types @(rf/subscribe [::sub/task-types])]
    [:div.border.rounded-lg.p-4
     [:h3.text-lg.font-semibold.mb-1 (loc/tr :settings/ai-admin-title)]
     [:p.text-sm.text-base-light-text-paler.mb-4 (loc/tr :settings/ai-admin-description)]
     (when error
       [:div.text-danger-regular.mb-3 error])
     (cond
       loading? [:div.text-base-light-text-paler (loc/tr :settings/ai-admin-loading)]
       :else
       [:div
        [service-editor]
        [services-table]
        [:div.mt-6
         [:div.font-semibold.mb-2 (loc/tr :settings/ai-admin-task-routing)]
         (for [tt task-types]
           ^{:key (:value tt)}
           [task-config-row tt])]
        [pipeline-overrides]])]))

(defn view
  []
  (r/create-class
   {:component-did-mount    (fn [_] (rf/dispatch [::event/init]))
    :component-will-unmount (fn [_] (rf/dispatch [::event/cleanup]))
    :reagent-render         (fn [] [panel])}))
